import { useEffect, useRef, useState } from 'react'
import type { Alert, AlertAction, AssignmentView, Denial, Incident, IncidentAction, LicenseView, Simulation, SimulationRequest, UnitView } from './api'
import { DataNote } from './DataNote'
import { alertText, translate, type Lang } from './i18n'
import { fmtDistance } from './units'
import type { SortMode } from './queue'
import { useShiftCodes } from './shifts'
import { agencyToday, fmtDate, fmtTime, isToday } from './time'

const ALERT_FLOW: Record<string, AlertAction[]> = {
  New: ['Acknowledge', 'Escalate', 'Resolve'],
  Acknowledged: ['Assign', 'Escalate', 'Resolve'],
  Assigned: ['Escalate', 'Resolve'],
  Escalated: ['Assign', 'Resolve'],
  Resolved: ['Close'],
  Closed: [],
}

const INCIDENT_FLOW: Record<string, IncidentAction[]> = {
  Reported: ['Validate', 'Activate', 'Cancel'],
  Validated: ['Activate', 'Cancel'],
  // Activo o controlado también se cancela —registrado en la unidad equivocada, reportado dos
  // veces—, con motivo, y la unidad vuelve al servicio (H-63).
  Active: ['Control', 'Complete', 'Cancel'],
  Controlled: ['Complete', 'Cancel'],
  Completed: [],
  Cancelled: [],
}

/** La hora, en la de la agencia o la de quien mira (H-01); la fecha solo si no es de hoy. */
function when(lang: Lang, iso: string | null): string {
  if (!iso) return ''
  return isToday(iso)
    ? fmtTime(iso, lang)
    : `${fmtDate(iso, lang, { month: 'short', day: 'numeric' })} ${fmtTime(iso, lang)}`
}

// ------------------------------------------------------------------ unidades

/** Estados de una visita que todavía se pueden pasar a otra unidad. En curso, no: primero termina o se interrumpe. */
const REASSIGNABLE = new Set(['Released', 'Acknowledged', 'Interrupted'])
/** Cancelar a mano: la interrumpida se reasigna o la reparte la reoptimización (H-128). */
const CANCELABLE = new Set(['Released', 'Acknowledged'])
/** Unidades que ya no reciben asignaciones. */
const INACTIVE = new Set(['EndShift', 'Unavailable'])

/** Lo que una unidad reporta por radio (H-62, H-63). */
export type RadioReport = 'incident' | 'unavailable' | 'restored'

/** Qué se le puede reportar por radio a una unidad en cada estado. */
function radioFor(status: string): RadioReport[] {
  if (status === 'Unavailable') return ['restored']
  if (status === 'OffDuty' || status === 'EndShift' || status === 'IncidentResponse') return []
  if (status === 'Returning') return ['incident']
  return ['incident', 'unavailable']
}

export function UnitsPanel({
  lang, units, assignments, selected, onSelect, loaded, problem, onReassign, busy = false, onRadio, onCancelVisit,
}: {
  lang: Lang
  units: UnitView[]
  assignments: AssignmentView[]
  selected: string | null
  onSelect: (id: string | null) => void
  loaded: boolean
  problem: Denial | null
  /** Solo para quien puede ajustar el plan (Watch Commander). Sin esto, la tarjeta no ofrece reasignar. */
  onReassign?: (assignment: AssignmentView, toPatrolUnitId: string, justification: string) => Promise<boolean>
  /** Solo para el Watch Commander: retirar la visita sin dársela a nadie (H-128). */
  onCancelVisit?: (assignment: AssignmentView, justification: string) => Promise<boolean>
  busy?: boolean
  /**
   * Para quien atiende incidentes: registrar lo que la unidad reportó por radio —un incidente, o
   * que queda fuera de servicio— con el mismo efecto que si llegara del teléfono (H-62, H-63).
   */
  onRadio?: (unit: UnitView, report: RadioReport, text: string) => Promise<boolean>
}) {
  const [reassigning, setReassigning] = useState<string | null>(null)
  const [radio, setRadio] = useState<{ unit: string; report: RadioReport } | null>(null)
  const [radioText, setRadioText] = useState('')
  const [toUnit, setToUnit] = useState('')
  const [why, setWhy] = useState('')
  // Lo que se acaba de reasignar. El aviso de App va a la pestaña Plan, y el jefe de turno, que
  // reasigna desde aquí, no veía nada: el formulario solo se cerraba segundos después.
  const [reasignada, setReasignada] = useState<string | null>(null)
  const [cancelando, setCancelando] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const needle = filter.trim().toLowerCase()
  const shown = units.filter((u) => !needle || u.callSign.toLowerCase().includes(needle) || u.division.toLowerCase().includes(needle))
  // La visita que toca: la que está en curso o, si no, la siguiente comunicada por secuencia. Antes
  // era «la primera que no está completada», en el orden en que llegaran: nombraba otra visita, o
  // una cancelada, y decía «En asignación» de una unidad que ni había llegado.
  const target = (u: UnitView) => {
    const suyas = assignments
      .filter((a) => a.patrolUnitId === u.patrolUnitId && ['Released', 'Acknowledged', 'InProgress', 'Interrupted'].includes(a.status) && a.label)
      .sort((a, b) => a.sequence - b.sequence)
    return suyas.find((a) => a.status === 'InProgress') ?? suyas[0]
  }

  return (
    <section className="panel">
      <h2>{translate(lang, 'units.title')} · {units.length}</h2>
      <p className="notice">{translate(lang, 'units.help')}</p>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={translate(lang, 'units.filter')}
        style={{ width: '100%', marginBottom: 8 }}
      />
      {/* El estado se decide con el total, no con lo filtrado: filtrar a cero no es «no hay
          unidades», y decirlo mientras la lista viene en camino tampoco. */}
      <DataNote lang={lang} loaded={loaded} problem={problem} count={units.length} empty="units.none" />
      {reasignada && <p className="notice ok-text" role="status">{reasignada}</p>}
      {units.length > 0 && shown.length === 0 && (
        <p className="notice">{translate(lang, 'units.noneMatch')}</p>
      )}
      <div className="rows">
        {shown.map((u) => (
          <div
            key={u.patrolUnitId}
            className="card"
            onClick={() => onSelect(selected === u.patrolUnitId ? null : u.patrolUnitId)}
            style={{ cursor: 'pointer', outline: selected === u.patrolUnitId ? '2px solid var(--info)' : undefined }}
          >
            <div className="row-line">
              <strong className="grow">{u.callSign} · {u.division}</strong>
              <span className={`chip ${u.connectivity === 'Online' ? 'ok' : u.connectivity === 'Stale' ? 'warning' : 'danger'}`}>
                {translate(lang, `connectivity.${u.connectivity}`)}
              </span>
            </div>
            <div className="meta">
              {translate(lang, `unit.${u.status}`)}
              {u.lastSeenAt ? ` · ${translate(lang, 'units.lastSeen', [when(lang, u.lastSeenAt)])}` : ` · ${translate(lang, 'units.noPosition')}`}
            </div>
            {u.status === 'Unavailable' && u.statusReason && (
              <div className="meta">{translate(lang, 'units.reason')}: {u.statusReason}</div>
            )}
            {target(u) && (
              <div className="meta">
                {translate(lang, target(u)!.status === 'InProgress' ? 'units.assignment'
                  : target(u)!.status === 'Interrupted' ? 'units.interrupted' : 'units.next')}: {target(u)!.label}
              </div>
            )}
            {onRadio && selected === u.patrolUnitId && radioFor(u.status).length > 0 && (
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6 }}>
                {radio?.unit !== u.patrolUnitId ? (
                  <div className="actions">
                    {radioFor(u.status).map((report) => (
                      <button
                        key={report}
                        disabled={busy}
                        onClick={() => {
                          // Volver al servicio no pide nada: se registra en el acto.
                          if (report === 'restored') void onRadio(u, report, '')
                          else { setRadio({ unit: u.patrolUnitId, report }); setRadioText('') }
                        }}
                      >
                        {translate(lang, `radio.${report}`)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="rows">
                    <textarea
                      value={radioText}
                      onChange={(e) => setRadioText(e.target.value)}
                      placeholder={translate(lang, radio.report === 'incident' ? 'radio.incidentWhat' : 'radio.unavailableWhy')}
                      aria-label={translate(lang, radio.report === 'incident' ? 'radio.incidentWhat' : 'radio.unavailableWhy')}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                    <p className="notice">{translate(lang, 'radio.help')}</p>
                    <div className="row-line">
                      <button
                        disabled={busy || !radioText.trim()}
                        onClick={() => void onRadio(u, radio.report, radioText.trim()).then((ok) => { if (ok) setRadio(null) })}
                      >
                        {translate(lang, 'radio.confirm')}
                      </button>
                      <button onClick={() => setRadio(null)}>{translate(lang, 'reassign.cancel')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {onReassign && selected === u.patrolUnitId && target(u) && REASSIGNABLE.has(target(u)!.status) && (
              // Dentro de la tarjeta, pero sin que un clic aquí la deseleccione.
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6 }}>
                {cancelando === target(u)!.assignmentId ? (
                  <div className="rows">
                    <textarea
                      value={why}
                      onChange={(e) => setWhy(e.target.value)}
                      placeholder={translate(lang, 'cancelVisit.why')}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                    <p className="notice">{translate(lang, 'cancelVisit.help')}</p>
                    <div className="row-line">
                      <button
                        className="danger"
                        disabled={busy || !why.trim()}
                        onClick={() => {
                          const visita = target(u)!
                          setReasignada(null)
                          void onCancelVisit!(visita, why.trim()).then((ok) => {
                            if (!ok) return
                            setCancelando(null)
                            setReasignada(translate(lang, 'cancelVisit.done', [visita.label, u.callSign]))
                          })
                        }}
                      >
                        {translate(lang, busy ? 'cancelVisit.working' : 'cancelVisit.confirm')}
                      </button>
                      <button onClick={() => setCancelando(null)}>{translate(lang, 'reassign.cancel')}</button>
                    </div>
                  </div>
                ) : reassigning !== target(u)!.assignmentId ? (
                  <div className="row-line">
                    <button onClick={() => { setReassigning(target(u)!.assignmentId); setToUnit(''); setWhy('') }}>
                      {translate(lang, 'reassign.open')}
                    </button>
                    {onCancelVisit && CANCELABLE.has(target(u)!.status) && (
                      <button onClick={() => { setCancelando(target(u)!.assignmentId); setWhy('') }}>
                        {translate(lang, 'cancelVisit.open')}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="rows">
                    <label>
                      {translate(lang, 'reassign.to')}{' '}
                      <select value={toUnit} onChange={(e) => setToUnit(e.target.value)}>
                        <option value="">—</option>
                        {units
                          .filter((o) => o.patrolUnitId !== u.patrolUnitId && !INACTIVE.has(o.status))
                          .map((o) => (
                            <option key={o.patrolUnitId} value={o.patrolUnitId}>
                              {o.callSign} · {translate(lang, `unit.${o.status}`)}
                            </option>
                          ))}
                      </select>
                    </label>
                    <textarea
                      value={why}
                      onChange={(e) => setWhy(e.target.value)}
                      placeholder={translate(lang, 'reassign.why')}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                    <p className="notice">{translate(lang, 'reassign.help')}</p>
                    <div className="row-line">
                      <button
                        disabled={busy || !toUnit || !why.trim()}
                        onClick={() => {
                          const visita = target(u)!
                          const destino = units.find((o) => o.patrolUnitId === toUnit)?.callSign ?? ''
                          setReasignada(null)
                          void onReassign(visita, toUnit, why.trim()).then((ok) => {
                            if (!ok) return
                            setReassigning(null)
                            setReasignada(translate(lang, 'reassign.sent', [visita.label, destino]))
                          })
                        }}
                      >
                        {translate(lang, busy ? 'reassign.working' : 'reassign.confirm')}
                      </button>
                      <button onClick={() => setReassigning(null)}>{translate(lang, 'reassign.cancel')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// ------------------------------------------------------------------ alertas (§10.1)

/**
 * Las que cierra una persona diciendo cómo se atendieron: el pedido de apoyo (H-33) y el aviso de
 * un incidente que la unidad no podía atender, que alguien tuvo que mandar a otra (29-sep).
 */
const NOTA_AL_CERRAR = new Set(['SupportRequested', 'IncidentSighted'])

export function AlertsPanel({
  lang, alerts, total, showAll, onShowAll, onAction, busy, selected, onSelect, located,
  query, onQuery, sort, onSort, onlyVisible, onOnlyVisible, nearby, onNearby, visitOf, samePlace, onSamePlace,
  canAct = true, loaded, problem, onReplan,
}: {
  lang: Lang
  /** A dónde se pide la reoptimización, si quien mira puede pedirla (alerta de unidad fuera de servicio). */
  onReplan?: () => void
  /** Si no, la cola se ve y se busca pero no se actúa: el Planner y el Chief vigilan sin operar. */
  canAct?: boolean
  /** Ya filtradas y ordenadas. */
  alerts: Alert[]
  /** Cuántas hay en total, para decir qué parte se está viendo. */
  total: number
  showAll: boolean
  onShowAll: (value: boolean) => void
  onAction: (alert: Alert, action: AlertAction, assignedTo: string, note: string) => void
  busy: boolean
  selected: string | null
  onSelect: (alertId: string | null) => void
  /** Las que tienen lugar en el mapa; el resto lo dice, para que nadie las busque en vano. */
  located: Set<string>
  query: string
  onQuery: (value: string) => void
  sort: SortMode
  onSort: (value: SortMode) => void
  onlyVisible: boolean
  onOnlyVisible: (value: boolean) => void
  /** Cuántas hay cerca de la elegida, y si se está viendo solo ese grupo. */
  nearby: { count: number; meters: number; only: boolean }
  onNearby: (only: boolean) => void
  /** Qué visita del plan falló: el plan revisita un hotspot varias veces en un turno. */
  visitOf: (alert: Alert) => { sequence: number; plannedArrivalAt: string } | null
  /** Cuántas alertas comparten el punto con la elegida, y si se está viendo solo ese grupo. */
  samePlace: { count: number; only: boolean }
  onSamePlace: (only: boolean) => void
  loaded: boolean
  problem: Denial | null
}) {
  const [assignee, setAssignee] = useState<Record<string, string>>({})
  // Cómo se atendió un pedido de apoyo: sin eso no se resuelve (H-33).
  const [nota, setNota] = useState<Record<string, string>>({})
  // Lo que no se hace sin decir por qué: escalar —el jefe de turno tiene que saber qué se le pide
  // (H-66)— y cerrar un pedido de apoyo, que es la seguridad de un oficial (H-33).
  const pideNota = (a: Alert, action: AlertAction) =>
    action === 'Escalate' || (NOTA_AL_CERRAR.has(a.kind) && (action === 'Resolve' || action === 'Close'))
  const cards = useRef(new Map<string, HTMLDivElement>())

  // Elegida desde el mapa: la tarjeta se trae a la vista.
  useEffect(() => {
    if (selected) cards.current.get(selected)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selected])

  return (
    <section className="panel">
      <h2>{translate(lang, 'alerts.title')} · {total}</h2>
      <p className="notice">{translate(lang, 'alerts.help')}</p>

      <input
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder={translate(lang, 'alerts.search')}
        style={{ width: '100%', marginBottom: 6 }}
      />
      <div className="row-line" style={{ marginBottom: 6 }}>
        <select value={sort} onChange={(e) => onSort(e.target.value as SortMode)} aria-label={translate(lang, 'alerts.sort')}>
          {(['severity', 'newest', 'oldest', 'unit'] as SortMode[]).map((mode) => (
            <option key={mode} value={mode}>{translate(lang, `alerts.sort.${mode}`)}</option>
          ))}
        </select>
        <span className="notice grow">{translate(lang, 'alerts.showing', [alerts.length, total])}</span>
      </div>
      <label className="notice" style={{ display: 'block' }}>
        <input type="checkbox" checked={onlyVisible} onChange={(e) => onOnlyVisible(e.target.checked)} /> {translate(lang, 'alerts.onlyVisible')}
      </label>
      <label className="notice" style={{ display: 'block', marginBottom: 8 }}>
        <input type="checkbox" checked={showAll} onChange={(e) => onShowAll(e.target.checked)} /> {translate(lang, 'alerts.showAll')}
      </label>

      <DataNote lang={lang} loaded={loaded} problem={problem} count={total} empty="alerts.none" />
      {total > 0 && alerts.length === 0 && (
        <p className="notice">{translate(lang, 'alerts.noneMatch')}</p>
      )}
      {alerts.map((a) => (
        <div
          key={a.alertId}
          ref={(node) => { if (node) cards.current.set(a.alertId, node); else cards.current.delete(a.alertId) }}
          className={`card ${a.severity === 'Critical' ? 'critical' : 'warning'} ${a.alertId === selected ? 'chosen' : ''}`}
          onClick={() => onSelect(a.alertId === selected ? null : a.alertId)}
          style={{ cursor: 'pointer' }}
        >
          <div className="row-line">
            <h3 className="grow">{a.callSign} · {translate(lang, `kind.${a.kind}`)}</h3>
            <span className="chip">{translate(lang, `status.${a.status}`)}</span>
          </div>
          <div className="meta">{alertText(lang, a)}</div>
          <div className="meta">
            {translate(lang, `severity.${a.severity}`)} · {when(lang, a.createdAt)}
            {a.assignedTo && ` · ${translate(lang, 'alerts.assignedTo', [a.assignedTo])}`}
          </div>
          {/* Dos visitas al mismo hotspot son dos desvíos distintos: la tarjeta dice cuál es cuál. */}
          {visitOf(a) && (
            <div className="meta">
              {translate(lang, 'alerts.visit', [visitOf(a)!.sequence, when(lang, visitOf(a)!.plannedArrivalAt)])}
            </div>
          )}
          {a.status !== 'Resolved' && a.status !== 'Closed' && !located.has(a.alertId) && (
            <div className="meta">{translate(lang, 'map.noAlertPosition')}</div>
          )}
          {a.alertId === selected && samePlace.count > 1 && (
            <div className="meta">
              {translate(lang, 'alerts.samePlace', [samePlace.count])}{' '}
              <button onClick={(e) => { e.stopPropagation(); onSamePlace(!samePlace.only) }}>
                {translate(lang, samePlace.only ? 'alerts.nearbyClear' : 'alerts.samePlaceOnly')}
              </button>
            </div>
          )}
          {/* Varias alertas en pocas cuadras rara vez son casualidad: se pueden mirar juntas. */}
          {a.alertId === selected && nearby.count > 1 && (
            <div className="meta">
              {translate(lang, 'alerts.nearby', [nearby.count, fmtDistance(nearby.meters, lang)])}{' '}
              <button onClick={(e) => { e.stopPropagation(); onNearby(!nearby.only) }}>
                {translate(lang, nearby.only ? 'alerts.nearbyClear' : 'alerts.nearbyOnly')}
              </button>
            </div>
          )}
          {/* Una unidad fuera de servicio con visitas: lo que sigue es repartirlas (29-sep). */}
          {a.kind === 'UnitUnavailable' && onReplan && a.status !== 'Resolved' && a.status !== 'Closed' && (
            <div className="actions">
              <button onClick={(e) => { e.stopPropagation(); onReplan() }}>{translate(lang, 'alerts.replan')}</button>
            </div>
          )}
          {canAct && <div className="actions">
            {(ALERT_FLOW[a.status] ?? []).map((action) => (
              <button
                key={action}
                disabled={busy || (action === 'Assign' && !(assignee[a.alertId] ?? '').trim())
                  || (pideNota(a, action) && !(nota[a.alertId] ?? '').trim())}
                onClick={(e) => { e.stopPropagation(); onAction(a, action, assignee[a.alertId] ?? '', nota[a.alertId] ?? '') }}
              >
                {translate(lang, `alerts.action.${action}`)}
              </button>
            ))}
            {(ALERT_FLOW[a.status] ?? []).includes('Assign') && (
              <input
                value={assignee[a.alertId] ?? ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setAssignee({ ...assignee, [a.alertId]: e.target.value })}
                placeholder={translate(lang, 'alerts.assignTo')}
                style={{ width: 150 }}
              />
            )}
            {(ALERT_FLOW[a.status] ?? []).some((x) => pideNota(a, x)) && (
              <input
                value={nota[a.alertId] ?? ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setNota({ ...nota, [a.alertId]: e.target.value })}
                placeholder={translate(lang, NOTA_AL_CERRAR.has(a.kind) ? 'alerts.supportNote' : 'alerts.escalateNote')}
                aria-label={translate(lang, NOTA_AL_CERRAR.has(a.kind) ? 'alerts.supportNote' : 'alerts.escalateNote')}
                style={{ flex: '1 1 220px' }}
              />
            )}
          </div>}
        </div>
      ))}
    </section>
  )
}

// ------------------------------------------------------------------ incidentes (§10.2)

/**
 * El estado de la licencia, cuando hay algo que decir (ADR-0004).
 *
 * Solo aparece si el backend marca `warn`: con la licencia vigente y lejos de vencer, el
 * operador no tiene por qué leer nada sobre contratos. Cuando aparece, dice qué pasa y hasta
 * dónde llega — nunca "contacte a su proveedor" sin más.
 */
export function LicenseBanner({ lang, license }: { lang: Lang; license: LicenseView | null }) {
  if (!license || !license.warn) return null

  const vencimiento = license.notAfter ?? ''
  const [clave, args]: [string, (string | number)[] | null] =
    license.state === 'Expired' ? ['license.expired', [vencimiento]]
    : license.state === 'Grace' ? ['license.grace', [vencimiento]]
    : license.state === 'Invalid' ? ['license.invalid', null]
    : license.state === 'Evaluation' ? ['license.evaluation', null]
    : ['license.expiring', [vencimiento, license.daysToExpiry ?? 0]]

  // Vencida del todo es lo único que impide trabajar; lo demás es un aviso.
  const gravedad = license.state === 'Expired' ? 'critical' : 'warning'

  return (
    <div className={`card ${gravedad}`}>
      <div className="meta">{translate(lang, clave, args)}</div>
      {license.soldTo && <div className="meta">{translate(lang, 'license.soldTo', [license.soldTo])}</div>}
    </div>
  )
}

/** Cuánto tiempo después de terminar un incidente se sigue sugiriendo reincorporar la unidad. */
const RETURN_HINT_MS = 2 * 3600_000

export function IncidentsPanel({
  lang, incidents, onAction, busy, canAct = true, loaded, problem, onReplan, selected = null, onSelect,
}: {
  lang: Lang
  canAct?: boolean
  incidents: Incident[]
  onAction: (incident: Incident, action: IncidentAction, note: string) => void
  busy: boolean
  loaded: boolean
  problem: Denial | null
  /**
   * Pedir la reoptimización que corresponde a este incidente (§15.1, pasos 4 y 7). Solo para
   * quien puede pedirla. La tarjeta ofrecía «Controlado / Completar» y ahí terminaba: el paso
   * siguiente había que saberlo (hallazgo H-31 de la prueba integral).
   */
  onReplan?: (incident: Incident, returning: boolean) => void
  /** El incidente elegido: el mapa lo centra y lo resalta. */
  selected?: string | null
  onSelect?: (incident: Incident) => void
}) {
  // Qué incidentes ya tienen su reoptimización pedida desde aquí, para no insistir.
  const [pedidas, setPedidas] = useState<ReadonlySet<string>>(new Set())
  const [motivo, setMotivo] = useState<Record<string, string>>({})
  const vuelve = (i: Incident) =>
    i.status === 'Completed' && i.completedAt !== null && Date.now() - new Date(i.completedAt).getTime() < RETURN_HINT_MS
  const clave = (i: Incident, returning: boolean) => `${i.incidentId}/${returning ? 'vuelve' : 'sale'}`
  return (
    <section className="panel">
      <h2>{translate(lang, 'incidents.title')}</h2>
      <p className="notice">{translate(lang, 'incidents.help')}</p>
      <DataNote lang={lang} loaded={loaded} problem={problem} count={incidents.length} empty="incidents.none" />
      {incidents.map((i) => (
        <div
          key={i.incidentId}
          className={`card ${i.status === 'Active' ? 'critical' : ''}`}
          // Elegirlo lo muestra en el mapa, como una unidad o una alerta. Solo si tiene dónde.
          {...(onSelect && i.latitude !== null && i.longitude !== null ? {
            role: 'button', tabIndex: 0, title: translate(lang, 'incidents.showOnMap'),
            onClick: (e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest('button')) onSelect(i) },
            onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' && e.target === e.currentTarget) onSelect(i) },
            style: { cursor: 'pointer', outline: selected === i.incidentId ? '2px solid var(--info)' : undefined },
          } : {})}
        >
          <div className="row-line">
            <h3 className="grow">{i.callSign}</h3>
            <span className="chip">{translate(lang, `status.${i.status}`)}</span>
          </div>
          <div className="meta">{i.description}</div>
          <div className="meta">{when(lang, i.reportedAt)}</div>
          {(i.latitude === null || i.longitude === null) && (
            <div className="meta">{translate(lang, 'incidents.noLocation')}</div>
          )}
          {canAct && <div className="actions">
            {(INCIDENT_FLOW[i.status] ?? []).map((action) => (
              <button
                key={action}
                // Cancelar pide motivo: el incidente no se borra, y su historia tiene que decir por qué (H-70).
                disabled={busy || (action === 'Cancel' && !(motivo[i.incidentId] ?? '').trim())}
                onClick={() => onAction(i, action, action === 'Cancel' ? (motivo[i.incidentId] ?? '') : '')}
              >
                {translate(lang, `incidents.action.${action}`)}
              </button>
            ))}
            {(INCIDENT_FLOW[i.status] ?? []).includes('Cancel') && (
              <input
                value={motivo[i.incidentId] ?? ''}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) => setMotivo({ ...motivo, [i.incidentId]: e.target.value })}
                placeholder={translate(lang, 'incidents.cancelNote')}
                aria-label={translate(lang, 'incidents.cancelNote')}
                style={{ flex: '1 1 220px' }}
              />
            )}
          </div>}
          {onReplan && (i.status === 'Active' || i.status === 'Controlled' || vuelve(i)) && (() => {
            const returning = i.status === 'Completed'
            const hecha = pedidas.has(clave(i, returning))
            return (
              <div className="notice" style={{ marginTop: 6 }}>
                {translate(lang, returning ? 'incidents.replanReturn' : 'incidents.replanOut', [i.callSign])}{' '}
                <button
                  disabled={busy || hecha}
                  onClick={() => { onReplan(i, returning); setPedidas(new Set(pedidas).add(clave(i, returning))) }}
                >
                  {translate(lang, hecha ? 'incidents.replanDone' : 'incidents.replanAsk')}
                </button>
              </div>
            )
          })()}
        </div>
      ))}
    </section>
  )
}

// ------------------------------------------------------------------ reoptimización (§15.1)

export function ReplanPanel({
  lang, onRequest, busy, result, currentShift,
}: {
  lang: Lang
  /** El turno que corre: es el que se reoptimiza casi siempre. */
  currentShift?: { shiftDate: string; shiftCode: string }
  onRequest: (shiftDate: string, shiftCode: string, reason: string) => void
  busy: boolean
  result: string | null
}) {
  const [date, setDate] = useState(() => currentShift?.shiftDate ?? agencyToday())
  const shifts = useShiftCodes()
  const [code, setCode] = useState(currentShift?.shiftCode ?? 'EVENING')
  const [reason, setReason] = useState('')
  // El turno llega del servidor después de pintar el panel, y cambia cuando empieza otro. Antes
  // se tomaba solo al montar y se quedaba en EVENING, con otro turno en la calle (H-34). Si la
  // persona eligió otro a mano, se respeta.
  const [tocado, setTocado] = useState(false)
  useEffect(() => {
    if (!currentShift || tocado) return
    setDate(currentShift.shiftDate)
    setCode(currentShift.shiftCode)
  }, [currentShift?.shiftDate, currentShift?.shiftCode, tocado])  

  return (
    <section className="panel">
      <h2>{translate(lang, 'replan.title')}</h2>
      <p className="notice">{translate(lang, 'replan.help')}</p>
      <div className="card">
        <div className="row-line" style={{ gap: 6, marginBottom: 6 }}>
          <input type="date" value={date} onChange={(e) => { setTocado(true); setDate(e.target.value) }} aria-label={translate(lang, 'replan.shiftDate')} />
          <select value={code} onChange={(e) => { setTocado(true); setCode(e.target.value) }} aria-label={translate(lang, 'replan.shiftCode')}>
            {shifts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={translate(lang, 'replan.reason')}
          style={{ width: '100%', marginBottom: 8 }}
        />
        <button className="primary" disabled={busy || !reason.trim()} onClick={() => onRequest(date, code, reason.trim())}>
          {translate(lang, 'replan.request')}
        </button>
        {result && <div className="meta" style={{ marginTop: 8 }}>{result}</div>}
      </div>
    </section>
  )
}

// ------------------------------------------------------------------ demostración

/**
 * Gobierna las unidades simuladas.
 *
 * Solo aparece si el servidor dice que este despliegue es de demostración: la misma compilación
 * sirve para producción, donde estos endpoints no existen y el panel no se muestra.
 */
export function DemoPanel({
  onFormShift, formResult,
  lang, simulation, busy, onStart, onStop,
}: {
  lang: Lang
  simulation: Simulation | null
  busy: boolean
  onStart: (request: SimulationRequest) => void
  onStop: () => void
  onFormShift: () => void
  formResult: string | null
}) {
  const [units, setUnits] = useState(8)
  const [pace, setPace] = useState(20)
  const [deviate, setDeviate] = useState(4)
  const running = simulation?.running === true

  return (
    <section className="panel">
      <h2>{translate(lang, 'demo.title')}</h2>
      <p className="notice">{translate(lang, 'demo.help')}</p>

      {/* Armar el turno: lo que antes exigía `tactix form` en el servidor. Preparar una
          demostración no debería obligar a nadie a abrir una consola. */}
      <div className="card">
        <button disabled={busy} onClick={onFormShift}>{translate(lang, 'demo.formShift')}</button>
        {formResult && <div className="meta">{formResult}</div>}
      </div>
      <div className="card">
        <div className="rows" style={{ marginBottom: 8 }}>
          <label className="row-line">
            <span className="grow notice">{translate(lang, 'demo.units')}</span>
            <input type="number" min={1} max={60} value={units} disabled={running}
              onChange={(e) => setUnits(Number(e.target.value))} style={{ width: 80 }} />
          </label>
          <label className="row-line">
            <span className="grow notice">{translate(lang, 'demo.pace')}</span>
            <input type="number" min={2} max={600} value={pace} disabled={running}
              onChange={(e) => setPace(Number(e.target.value))} style={{ width: 80 }} />
          </label>
          <label className="row-line">
            <span className="grow notice">{translate(lang, 'demo.deviate')}</span>
            <select value={deviate} disabled={running} onChange={(e) => setDeviate(Number(e.target.value))}>
              <option value={0}>{translate(lang, 'demo.deviateNone')}</option>
              {[2, 3, 4, 6, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>

        {running ? (
          <button className="danger" disabled={busy} onClick={onStop}>{translate(lang, 'demo.stop')}</button>
        ) : (
          <button className="primary" disabled={busy}
            onClick={() => onStart({ units, paceSeconds: pace, deviateEvery: deviate, shiftCode: 'EVENING' })}>
            {translate(lang, 'demo.start')}
          </button>
        )}

        <div className="meta" style={{ marginTop: 8 }}>
          {running
            ? translate(lang, 'demo.running', [simulation.units.length, simulation.visits])
            : translate(lang, 'demo.idle')}
        </div>
        {running && simulation.deviating.length > 0 && (
          <div className="meta">{translate(lang, 'demo.deviating', [simulation.deviating.join(', ')])}</div>
        )}
        {running && simulation.log.length > 0 && (
          <pre className="demo-log">{simulation.log.slice(-8).map((line) => line).join(`
`)}</pre>
        )}
      </div>
    </section>
  )
}
